import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { LoadingPanels } from "~/components/analytics/shared/LoadingStates";
import { Build } from "~/components/build/Show/lib/types";
import { CommandJob } from "~/components/build/Show/lib/types/CommandJob";
import { useReactRouterNavigateHandler } from "~/components/build/Show/lib/useReactRouterNavigateHandler";

type Props = {
  build: Build;
  job?: CommandJob;
};

// These are params that we want to pass to the turbo frame.
// See Analytics::ViewConfig for more details.
const VIEW_CONFIG_PARAMS = [
  "tags",
  "labels",
  "state",
  "owners",
  "suites",
  "period",
  "branch",
  "sort_by",
  "order",
  "query",
  "columns[]",
  "display_properties[]",
  "group_by",
  "expand_failures",
];

export default function TestEngine({ build, job }: Props) {
  const navigateClickHandler = useReactRouterNavigateHandler();
  const [searchParams] = useSearchParams();

  const viewConfigFromBrowser = useMemo(() => {
    const permitted_params = Array.from(searchParams.entries()).filter(([key]) =>
      VIEW_CONFIG_PARAMS.includes(key),
    );
    return new URLSearchParams(permitted_params).toString();
  }, [searchParams]);

  const defaultTestTabUrl = job
    ? job.testEngine.testTabUrls?.tabUrl
    : build.testEngine.testTabUrls?.tabUrl;
  const frameId = `te_tests${job ? `_${job.id}` : ""}`;

  const syncViewConfigFromTurboFrameToBrowser = useCallback((src) => {
    const currentFrameUrl = new URL(src);
    const currentTurboFrameParams = new URLSearchParams(currentFrameUrl.search);

    const currentBrowserUrl = new URL(window.location.href);
    const currentBrowserParams = new URLSearchParams(currentBrowserUrl.search);

    const newParams = new URLSearchParams();

    // Get the existing browser params that are not a view config param
    // and add them to the new params
    currentBrowserParams.forEach((value, key) => {
      if (!VIEW_CONFIG_PARAMS.includes(key)) {
        newParams.append(key, value);
      }
    });

    // Get the view config params from the turbo frame
    // and add them to the new params
    currentTurboFrameParams.forEach((value, key) => {
      if (VIEW_CONFIG_PARAMS.includes(key)) {
        newParams.append(key, value);
      }
    });

    currentBrowserUrl.search = newParams.toString();
    window.history.replaceState({}, "", currentBrowserUrl.toString());
  }, []);

  const turboUrl = useMemo(() => {
    if (!defaultTestTabUrl) {
      return null;
    }

    const url = new URL(defaultTestTabUrl, window.location.origin);

    // The default test tab URL is defined by the backend (Analytics::InternalApi#build_test_tab_urls).
    // This URL contains default params (i.e. default test tab saved view config).
    // We should only apply those default params when the current browser URL doesn't have any of the view config params,
    // otherwise we might override the user's current view config with the defaults view config.
    if (viewConfigFromBrowser !== "") {
      url.search = viewConfigFromBrowser;
    }

    return url.toString();
  }, [defaultTestTabUrl, viewConfigFromBrowser]);

  useEffect(() => {
    const handler = (event) => {
      if (event.target.id === frameId) {
        localStorage.setItem("hide-test-tab-pill", "true");

        syncViewConfigFromTurboFrameToBrowser(event.target.src);
      }
    };

    document.addEventListener("turbo:frame-load", handler);
    return () => document.removeEventListener("turbo:frame-load", handler);
  }, [frameId, job, syncViewConfigFromTurboFrameToBrowser]);

  if (!turboUrl) {
    return null;
  }

  return (
    <div data-turbo="true" onClick={navigateClickHandler} className={job ? "mt-2" : ""}>
      <turbo-frame
        className="h-full w-full"
        id={frameId}
        src={turboUrl}
        data-executions={true}
        data-controller="error"
        data-action="turbo:frame-missing->error#handleTurboFrameMissing"
      >
        <div className="p-4">
          <LoadingPanels rows={10} columns={1} />
        </div>
      </turbo-frame>
    </div>
  );
}
